// src/modules/accounting/pages/BalanceSheetPage.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Spin, Alert, Typography, Row, Col, DatePicker, Button, Space,
  Statistic, Tag, Empty, Tooltip, Divider
} from 'antd';
import {
  ReloadOutlined, CheckCircleOutlined, WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getCategorisedBalanceSheet, verifyAccountingEquation } from '../api';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text } = Typography;

export default function BalanceSheetPage() {
  const [data, setData] = useState(null);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [asOfDate, setAsOfDate] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (asOfDate) params.asOfDate = asOfDate.endOf('day').toISOString();
      const [bsRes, verifRes] = await Promise.all([
        getCategorisedBalanceSheet(params),
        verifyAccountingEquation(params),
      ]);
      setData(bsRes.data || bsRes);
      setVerification(verifRes.data || verifRes);
    } catch (err) {
      setError(err.message || 'Failed to load balance sheet');
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  useEffect(() => { fetch(); }, [fetch]);

  const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;
  if (error) return <Alert type="error" message={error} showIcon />;
  if (!data) return <Empty />;

  const renderSection = (title, color, section) => {
    if (!section) return null;

    // Items come back with nested account object — flatten for Ant table
    const flatItems = (items = []) =>
      items.map((item, i) => ({
        ...item.account,
        balance: item.balance,
        isGroup: item.isGroup,
        rollupBalance: item.rollupBalance,
        descendantCount: item.descendantCount,
        isChild: !!item.account?.parentAccount,
        key: i,
      }));

    const itemColumns = [
      { title: 'Code', dataIndex: 'code', width: 80 },
      {
        title: 'Account',
        dataIndex: 'name',
        render: (name, rec) => (
          <Space size={4}>
            {rec.isChild && <Text type="secondary" style={{ fontFamily: 'monospace' }}>↳</Text>}
            <span>{name}</span>
            {rec.isGroup && (
              <Tooltip title={`Group heading covering ${rec.descendantCount} child account(s). The children are listed separately and are what the section total adds up — this figure is a subtotal, not an extra amount.`}>
                <Tag color="geekblue">Group</Tag>
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: 'Balance', dataIndex: 'balance', align: 'right', width: 160,
        render: (v, rec) => {
          // A group account carries no postings of its own. Showing its bare ₹0
          // is what hid the branch total; show the roll-up, muted, instead.
          if (rec.isGroup && v === 0 && rec.rollupBalance !== 0) {
            return <Text type="secondary" italic>₹{fmt(rec.rollupBalance)}</Text>;
          }
          return `₹${fmt(v)}`;
        },
      },
    ];

    // Assets / Liabilities come back as { categories: [{category, items, subtotal}], total }
    // Equity comes back as { items: [...], subtotal, netIncome, total }
    const categories = section.categories;
    const directItems = !categories ? section.items : null;

    return (
      <Card
        title={<><Tag color={color}>{title}</Tag> <Text strong>₹{fmt(section.total)}</Text></>}
        size="small"
        style={{ marginBottom: 12 }}
      >
        {categories && categories.map((cat) => (
          <div key={cat.category} style={{ marginBottom: 8 }}>
            <Text strong style={{ fontSize: 12 }}>
              {cat.category} — ₹{fmt(cat.subtotal)}
            </Text>
            <DataTable persistId="accounting.balance-sheet.items"
              dataSource={flatItems(cat.items)}
              columns={itemColumns}
              pagination={false}
              size="small"
              showHeader={false}
            />
          </div>
        ))}
        {directItems && (
          <DataTable persistId="accounting.balance-sheet.items"
            dataSource={flatItems(directItems)}
            columns={itemColumns}
            pagination={false}
            size="small"
            showHeader={false}
          />
        )}
      </Card>
    );
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={5} style={{ margin: 0 }}>Balance Sheet</Title></Col>
        <Col>
          <Space>
            <DatePicker value={asOfDate} onChange={setAsOfDate} format="DD/MM/YYYY" placeholder="As of date" allowClear />
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetch} /></Tooltip>
          </Space>
        </Col>
      </Row>

      {/* Verification */}
      {verification && (
        <Card size="small" style={{ marginBottom: 16, background: verification.isBalanced ? '#f6ffed' : '#fff2e8' }}>
          <Row gutter={16} align="middle">
            <Col>
              {verification.isBalanced
                ? <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                : <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />}
            </Col>
            <Col flex="auto">
              <Text strong>{verification.isBalanced ? 'Accounting Equation Verified' : 'Accounting Equation IMBALANCED'}</Text>
              <br />
              <Text code>{verification.equation}</Text>
            </Col>
          </Row>
        </Card>
      )}

      {/* Assets */}
      {renderSection('ASSETS', 'blue', data.assets)}

      {/* Liabilities */}
      {renderSection('LIABILITIES', 'red', data.liabilities)}

      {/* Equity */}
      {renderSection('EQUITY', 'purple', data.equity)}

      {/* Net Income */}
      {data.equity?.netIncome != null && (
        <Card size="small" style={{ marginBottom: 12 }}>
          <Row justify="space-between">
            <Col><Text strong>Net Income (rolls into Equity)</Text></Col>
            <Col><Text strong style={{ color: data.equity?.netIncome >= 0 ? '#3f8600' : '#cf1322' }}>₹{fmt(data.equity?.netIncome)}</Text></Col>
          </Row>
        </Card>
      )}

      <Divider />

      {/* Ratios */}
      {data.ratios && (
        <>
          <Title level={5}>Financial Ratios</Title>
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="Working Capital" value={fmt(data.ratios.workingCapital)} prefix="₹" valueStyle={{ color: data.ratios.workingCapital >= 0 ? '#3f8600' : '#cf1322' }} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="Current Ratio" value={data.ratios.currentRatio?.toFixed(2) || '—'} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="Debt-to-Equity" value={data.ratios.debtToEquity?.toFixed(2) || '—'} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic title="Equation Balanced"
                  value={data.isBalanced ? 'Yes' : 'No'}
                  valueStyle={{ color: data.isBalanced ? '#52c41a' : '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Bottom Summary */}
      <Card size="small" style={{ marginTop: 16, background: '#fafafa' }}>
        <Row justify="end" gutter={32}>
          <Col><Statistic title="Total Assets" value={fmt(data.assets?.total)} prefix="₹" valueStyle={{ fontWeight: 'bold' }} /></Col>
          <Col><Statistic title="Total Liabilities" value={fmt(data.liabilities?.total)} prefix="₹" /></Col>
          <Col><Statistic title="Total Equity" value={fmt(data.equity?.total)} prefix="₹" /></Col>
        </Row>
      </Card>
    </div>
  );
}
